import type { UserProfile } from '@pharmaconnect/types'
import { Button, cn, useToast } from '@pharmaconnect/ui/web'
import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { NotificationsBell } from '../components/NotificationsBell'
import { PendingSalesBanner } from '../components/PendingSalesBanner'
import { useInbox } from '../hooks/useInbox'
import { useTranslation } from '../i18n/I18nProvider'
import { useAuthStore } from '../stores/authStore'

type Role = UserProfile['role']

interface NavItem {
  to: string
  labelKey?: string
  fallback: string
  end?: boolean
  /** Si défini, seuls ces rôles voient l'item. */
  roles?: ReadonlyArray<Role>
  group: string
}

interface SectionMeta {
  id: string
  label: string
  icon: string
}

// =============================================================================
// PHARMACIST NAV — owner / manager / cashier / doctor
// =============================================================================
const PHARMACIST_SECTIONS: ReadonlyArray<SectionMeta> = [
  { id: 'activity', label: 'Activité', icon: '🟢' },
  { id: 'stocks', label: 'Stocks & approvisionnement', icon: '📦' },
  { id: 'finance', label: 'Finances', icon: '💰' },
  { id: 'patients', label: 'Patients', icon: '🧑' },
  { id: 'pilot', label: 'Pilotage', icon: '📊' },
  { id: 'marketing', label: 'Marketing', icon: '📣' },
  { id: 'tools', label: 'Outils', icon: '🛠' },
  { id: 'doctor', label: 'Médecin', icon: '🩺' },
]

const PHARMACIST_NAV: ReadonlyArray<NavItem> = [
  // Activité
  { to: '/', labelKey: 'nav.dashboard', fallback: 'Dashboard', end: true, roles: ['owner', 'manager'], group: 'activity' },
  { to: '/inbox', labelKey: 'nav.inbox', fallback: 'Inbox', roles: ['owner', 'manager'], group: 'activity' },
  { to: '/caisse', labelKey: 'nav.cashier', fallback: 'Caisse', roles: ['owner', 'manager', 'cashier'], group: 'activity' },
  { to: '/cloture', labelKey: 'nav.cash_closure', fallback: 'Clôture caisse', roles: ['owner', 'manager', 'cashier'], group: 'activity' },
  { to: '/credits', labelKey: 'nav.customer_credits', fallback: 'Crédits clients', roles: ['owner', 'manager', 'cashier'], group: 'activity' },
  { to: '/livraisons', labelKey: 'nav.deliveries', fallback: 'Livraisons', roles: ['owner', 'manager', 'cashier'], group: 'activity' },
  { to: '/ordonnances', labelKey: 'nav.prescriptions', fallback: 'Ordonnances', roles: ['owner', 'manager'], group: 'activity' },
  { to: '/tiers-payant', labelKey: 'nav.insurance_claims', fallback: 'Tiers payant', roles: ['owner', 'manager'], group: 'activity' },
  { to: '/devis', labelKey: 'nav.proforma', fallback: 'Devis', roles: ['owner', 'manager'], group: 'activity' },

  // Stocks
  { to: '/stocks', labelKey: 'nav.stocks', fallback: 'Stocks', roles: ['owner', 'manager'], group: 'stocks' },
  { to: '/inventaire', labelKey: 'nav.inventory', fallback: 'Inventaire', roles: ['owner', 'manager'], group: 'stocks' },
  { to: '/lots', labelKey: 'nav.batches', fallback: 'Lots', roles: ['owner', 'manager'], group: 'stocks' },
  { to: '/fournisseurs', labelKey: 'nav.suppliers', fallback: 'Fournisseurs', roles: ['owner', 'manager'], group: 'stocks' },
  { to: '/commandes-achat', labelKey: 'nav.purchase_orders', fallback: 'Bons de commande', roles: ['owner', 'manager'], group: 'stocks' },
  { to: '/retours-fournisseurs', fallback: '↩ Retours fournisseurs', roles: ['owner', 'manager'], group: 'stocks' },
  { to: '/marketplace', fallback: '🤝 Marketplace inter-pharmacies', roles: ['owner', 'manager'], group: 'stocks' },
  { to: '/reappro', labelKey: 'nav.auto_replenish', fallback: 'Réappro auto', roles: ['owner', 'manager'], group: 'stocks' },
  { to: '/seuils-dynamiques', fallback: 'Seuils dynamiques', roles: ['owner', 'manager'], group: 'stocks' },
  { to: '/destructions', fallback: '🗑 Destructions', roles: ['owner', 'manager'], group: 'stocks' },
  { to: '/tarifs-bulk', fallback: '📈 MAJ tarifs en masse', roles: ['owner'], group: 'stocks' },
  { to: '/pricing-peremption', fallback: '📉 Pricing dynamique péremption', roles: ['owner', 'manager'], group: 'stocks' },

  // Finances
  { to: '/paiements', labelKey: 'nav.payments', fallback: 'Paiements', roles: ['owner', 'manager'], group: 'finance' },
  { to: '/comptabilite', labelKey: 'nav.accounting', fallback: 'Comptabilité', roles: ['owner', 'manager'], group: 'finance' },
  { to: '/tva', labelKey: 'nav.tva', fallback: 'TVA', roles: ['owner', 'manager'], group: 'finance' },
  { to: '/exports-comptables', fallback: 'Exports compta', roles: ['owner', 'manager'], group: 'finance' },
  { to: '/rapports', fallback: 'Rapports trimestriels', roles: ['owner'], group: 'finance' },
  { to: '/bilans-annuels', fallback: '📑 Bilan annuel SYSCOHADA', roles: ['owner'], group: 'finance' },

  // Patients
  { to: '/notation', labelKey: 'nav.ratings', fallback: 'Notation pharmacie', roles: ['owner', 'manager'], group: 'patients' },
  { to: '/adherence-alerts', fallback: '⚠️ Patients à risque', roles: ['owner', 'manager'], group: 'patients' },
  { to: '/nps', fallback: '⭐ NPS post-vente', roles: ['owner', 'manager'], group: 'patients' },
  { to: '/rdv', fallback: 'Rendez-vous', roles: ['owner', 'manager'], group: 'patients' },
  { to: '/vaccins-patients', fallback: '💉 Vaccins patients', roles: ['owner', 'manager'], group: 'patients' },
  { to: '/stats-vaccins', fallback: '📊 Stats vaccins', roles: ['owner', 'manager'], group: 'pilot' },
  { to: '/kiosque-admin', fallback: 'Kiosque accueil', roles: ['owner', 'manager'], group: 'patients' },
  { to: '/fidelite', labelKey: 'nav.loyalty', fallback: 'Fidélité', roles: ['owner', 'manager'], group: 'patients' },
  { to: '/recompenses', labelKey: 'nav.rewards', fallback: 'Récompenses', roles: ['owner', 'manager'], group: 'patients' },

  // Pilotage
  { to: '/analytics', labelKey: 'nav.analytics', fallback: 'Analytics', roles: ['owner', 'manager'], group: 'pilot' },
  { to: '/anomalies', labelKey: 'nav.anomalies', fallback: 'Anomalies', roles: ['owner', 'manager'], group: 'pilot' },
  { to: '/rh', labelKey: 'nav.hr', fallback: 'RH', roles: ['owner'], group: 'pilot' },
  { to: '/conges', fallback: '📅 Congés & absences', roles: ['owner', 'manager'], group: 'pilot' },
  { to: '/caissiers', labelKey: 'nav.cashiers', fallback: 'Caissiers', roles: ['owner'], group: 'pilot' },
  { to: '/audit', labelKey: 'nav.audit', fallback: 'Audit', roles: ['owner'], group: 'pilot' },

  // Marketing
  { to: '/campagnes', labelKey: 'nav.sms_campaigns', fallback: 'Campagnes SMS', roles: ['owner'], group: 'marketing' },
  { to: '/promotions', fallback: '🎟 Promotions & coupons', roles: ['owner', 'manager'], group: 'marketing' },

  // Outils
  { to: '/assistant', labelKey: 'nav.assistant_ai', fallback: 'Assistant IA', roles: ['owner', 'manager'], group: 'tools' },
  { to: '/sauvegardes', labelKey: 'nav.backups', fallback: 'Sauvegardes', roles: ['owner', 'manager'], group: 'tools' },
  { to: '/formation', labelKey: 'nav.formation', fallback: 'Formation', roles: ['owner', 'manager', 'cashier'], group: 'tools' },
  { to: '/mes-conges', fallback: '📅 Mes congés', roles: ['owner', 'manager', 'cashier', 'doctor'], group: 'tools' },
  { to: '/parametres', labelKey: 'nav.settings', fallback: 'Paramètres', roles: ['owner', 'manager', 'doctor'], group: 'tools' },
  { to: '/notifications', labelKey: 'nav.notifications', fallback: 'Notifications log', roles: ['owner', 'manager'], group: 'tools' },

  // Médecin (visible uniquement role=doctor)
  { to: '/mes-patients', fallback: 'Mes patients', roles: ['doctor'], group: 'doctor' },
  { to: '/prescrire', fallback: 'Émettre ordonnance', roles: ['doctor'], group: 'doctor' },
  { to: '/mes-templates', fallback: 'Mes templates', roles: ['doctor'], group: 'doctor' },
  { to: '/mes-grossesses', fallback: '🤰 Grossesses suivies', roles: ['doctor'], group: 'doctor' },
  { to: '/statistiques-medecin', fallback: 'Mes statistiques', roles: ['doctor'], group: 'doctor' },
]

// =============================================================================
// SUPER ADMIN NAV — vue système globale, séparée du dashboard pharmacien
// =============================================================================
const SUPER_ADMIN_SECTIONS: ReadonlyArray<SectionMeta> = [
  { id: 'overview', label: "Vue d'ensemble", icon: '📊' },
  { id: 'pharmacies', label: 'Réseau pharmacies', icon: '🏥' },
  { id: 'actors', label: 'Acteurs', icon: '👥' },
  { id: 'catalogs', label: 'Catalogues nationaux', icon: '📚' },
  { id: 'health', label: 'Santé publique', icon: '🚨' },
  { id: 'system', label: 'Système', icon: '🛡' },
]

const SUPER_ADMIN_NAV: ReadonlyArray<NavItem> = [
  // Vue d'ensemble — landing super_admin
  { to: '/admin', fallback: 'Tableau de bord', end: true, group: 'overview' },
  { to: '/admin/dci-stats', fallback: '📊 Stats DCI nationales', group: 'overview' },

  // Réseau pharmacies
  { to: '/admin/pharmacies', fallback: 'Liste pharmacies', group: 'pharmacies' },
  { to: '/admin/onboard-pharmacy', fallback: 'Onboard pharmacie', group: 'pharmacies' },

  // Acteurs
  { to: '/medecins', fallback: 'Médecins', group: 'actors' },
  { to: '/livreurs', fallback: 'Livreurs', group: 'actors' },

  // Catalogues nationaux
  { to: '/admin/quiz', fallback: '📚 Quiz formation', group: 'catalogs' },
  { to: '/admin/dci-catalog', fallback: '💊 Catalogue DCI', group: 'catalogs' },
  { to: '/admin/interactions', fallback: '⚠ Interactions DCI', group: 'catalogs' },
  { to: '/admin/assurances', fallback: 'Catalogue assurances', group: 'catalogs' },
  { to: '/admin/recalls', fallback: 'Rappels médicaments', group: 'catalogs' },
  { to: '/admin/vaccines', fallback: '💉 Calendrier vaccinal', group: 'catalogs' },

  // Santé publique
  { to: '/alertes', fallback: 'Alertes santé', group: 'health' },
  { to: '/pharmacovigilance', fallback: '⚠️ Pharmacovigilance', group: 'health' },
  { to: '/admin/duty-schedule', fallback: '🏥 Carnet de garde', group: 'health' },
  { to: '/admin/vaccine-coverage', fallback: '💉 Couverture vaccinale', group: 'health' },
  { to: '/admin/blood-donations', fallback: '🩸 Dons de sang', group: 'health' },
  { to: '/admin/blood-stats', fallback: '📊 Stats dons sang', group: 'health' },
  { to: '/admin/broadcast-owners', fallback: 'Broadcast owners', group: 'health' },

  // Système
  { to: '/admin/suspect-prescriptions', fallback: '🕵 Ordonnances suspectes', group: 'system' },
  { to: '/admin/erreurs', fallback: 'Logs erreurs', group: 'system' },
  { to: '/admin/notifications-dlq', fallback: 'Notifications DLQ', group: 'system' },
  { to: '/admin/webhooks', fallback: 'Webhooks', group: 'system' },
  { to: '/admin/security', fallback: 'Sécurité', group: 'system' },
  { to: '/admin/formation', fallback: 'Formation (admin)', group: 'system' },
  { to: '/audit', fallback: 'Audit logs', group: 'system' },
]

const COLLAPSED_KEY = 'pharmaconnect.nav.collapsed'

// Toutes les sections déclarées (pharmacien + super_admin) — utilisé pour
// le défaut "tout collapsé" à la première visite. La section de la route
// courante sera ré-ouverte automatiquement par l'effet d'auto-expand.
const ALL_SECTION_IDS = [
  'activity',
  'stocks',
  'finance',
  'patients',
  'pilot',
  'marketing',
  'tools',
  'doctor',
  'overview',
  'pharmacies',
  'actors',
  'catalogs',
  'health',
  'system',
]

const readCollapsed = (): Set<string> => {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = window.localStorage.getItem(COLLAPSED_KEY)
    if (raw === null) {
      // Première visite : tout collapsé. L'auto-expand sur route active
      // ré-ouvrira la section pertinente.
      return new Set(ALL_SECTION_IDS)
    }
    return new Set(JSON.parse(raw) as string[])
  } catch {
    return new Set()
  }
}

export function DashboardLayout() {
  const profile = useAuthStore((s) => s.profile)
  const logout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const inbox = useInbox(profile?.pharmacy_id ?? null)
  const { t, lang } = useTranslation()
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsed)
  // V134 — recherche locale dans la sidebar (filtrage live des items)
  const [search, setSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  // V136 — raccourci Cmd+K / Ctrl+K focus la recherche sidebar
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        searchRef.current?.focus()
        searchRef.current?.select()
      }
      if (e.key === 'Escape' && document.activeElement === searchRef.current) {
        setSearch('')
        searchRef.current?.blur()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const isSuperAdmin = profile?.role === 'super_admin'

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(Array.from(collapsed)))
    }
  }, [collapsed])

  const handleLogout = async () => {
    await logout()
    toast.info(lang === 'en' ? 'Signed out' : 'Déconnexion réussie')
    navigate('/login', { replace: true })
  }

  const labelFor = (item: NavItem): string => {
    if (!item.labelKey) return item.fallback
    const translated = t(item.labelKey)
    return translated === item.labelKey ? item.fallback : translated
  }

  const toggle = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Choisit le bon NAV selon le rôle
  const { nav, sections } = useMemo(() => {
    if (isSuperAdmin) {
      return { nav: SUPER_ADMIN_NAV, sections: SUPER_ADMIN_SECTIONS }
    }
    return { nav: PHARMACIST_NAV, sections: PHARMACIST_SECTIONS }
  }, [isSuperAdmin])

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase()
    const visible = nav.filter((item) => {
      if (item.roles && profile && !item.roles.includes(profile.role)) return false
      if (item.roles && !profile) return false
      if (!q) return true
      // Match sur fallback ou label traduit
      const labelTr = item.labelKey ? t(item.labelKey) : item.fallback
      const haystack = `${item.fallback} ${labelTr} ${item.to}`.toLowerCase()
      return haystack.includes(q)
    })
    return sections
      .map((s) => ({ ...s, items: visible.filter((i) => i.group === s.id) }))
      .filter((s) => s.items.length > 0)
  }, [nav, sections, profile, search, t])

  // Quand on recherche, déplier toutes les sections qui matchent (sinon
  // les résultats restent invisibles dans des accordions fermés).
  const effectiveCollapsed = useMemo(
    () => (search.trim() ? new Set<string>() : collapsed),
    [search, collapsed],
  )

  // Auto-expand la section qui contient la route active
  useEffect(() => {
    const active = nav.find((i) => i.to === location.pathname)
    if (active) {
      setCollapsed((prev) => {
        if (!prev.has(active.group)) return prev
        const next = new Set(prev)
        next.delete(active.group)
        return next
      })
    }
  }, [location.pathname, nav])

  const sidebarTitle = isSuperAdmin ? '👑 PharmaConnect Admin' : 'PharmaConnect'
  const sidebarSubtitle = isSuperAdmin ? 'Console système nationale' : 'Dashboard pharmacien'
  const sidebarBg = isSuperAdmin
    ? 'border-amber-500/20 bg-gradient-to-b from-amber-950/40 via-black/40 to-black/30'
    : 'border-white/10 bg-black/30'

  return (
    <div className="flex min-h-screen bg-pharmacist-bg text-neutral-100">
      <aside className={`flex w-64 flex-col border-r px-3 py-6 ${sidebarBg}`}>
        <div className="mb-6 px-2">
          <h1 className={`text-xl font-bold ${isSuperAdmin ? 'text-amber-400' : 'text-pharmacist'}`}>
            {sidebarTitle}
          </h1>
          <p className="text-xs text-neutral-400">{sidebarSubtitle}</p>
        </div>
        {/* V134/V136 — Recherche live dans la sidebar + raccourci Cmd+K */}
        <div className="mb-3 px-1">
          <div className="relative">
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Chercher un menu…"
              className="h-9 w-full rounded-md border border-white/10 bg-black/30 pl-9 pr-14 text-sm text-white placeholder:text-neutral-500 focus:border-pharmacist focus:outline-none focus:ring-1 focus:ring-pharmacist"
              data-testid="sidebar-search"
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"
            >
              🔍
            </span>
            {search ? (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-neutral-400 hover:bg-white/5 hover:text-white"
                aria-label="Effacer la recherche"
              >
                ×
              </button>
            ) : (
              <kbd
                aria-hidden="true"
                className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono text-[10px] font-bold text-neutral-400"
              >
                ⌘K
              </kbd>
            )}
          </div>
        </div>

        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto">
          {search.trim() && groups.length === 0 ? (
            <p className="px-3 py-2 text-xs text-neutral-500" data-testid="sidebar-search-empty">
              Aucun menu ne correspond à « {search} »
            </p>
          ) : null}
          {groups.map((g) => {
            const isCollapsed = effectiveCollapsed.has(g.id)
            return (
              <div key={g.id} className="mb-1">
                <button
                  type="button"
                  onClick={() => toggle(g.id)}
                  className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500 transition hover:bg-white/5 hover:text-neutral-300"
                  data-testid={`nav-section-${g.id}`}
                >
                  <span>
                    {g.icon} {g.label}
                  </span>
                  <span className="text-[8px]">{isCollapsed ? '▶' : '▼'}</span>
                </button>
                {!isCollapsed ? (
                  <ul className="mt-0.5 flex flex-col gap-0.5">
                    {g.items.map((item) => {
                      const showBadge = item.to === '/inbox' && inbox.total > 0
                      return (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            end={item.end}
                            className={({ isActive }) =>
                              `flex items-center justify-between rounded-md px-3 py-1.5 text-sm transition ${
                                isActive
                                  ? isSuperAdmin
                                    ? 'bg-amber-500/15 font-semibold text-amber-400'
                                    : 'bg-pharmacist/15 font-semibold text-pharmacist'
                                  : 'text-neutral-300 hover:bg-white/5'
                              }`
                            }
                          >
                            <span className="truncate">{labelFor(item)}</span>
                            {showBadge ? (
                              <span
                                className={cn(
                                  'ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums',
                                  inbox.maxSeverity === 'high'
                                    ? 'bg-offline text-white'
                                    : inbox.maxSeverity === 'medium'
                                      ? 'bg-amber-400 text-neutral-900'
                                      : 'bg-info text-white',
                                )}
                                data-testid="inbox-badge"
                              >
                                {inbox.total}
                              </span>
                            ) : null}
                          </NavLink>
                        </li>
                      )
                    })}
                  </ul>
                ) : null}
              </div>
            )
          })}
        </nav>
        {profile ? (
          <div className="mt-4 flex flex-col gap-3 border-t border-white/10 pt-4">
            <div className="px-2">
              <p className="text-sm font-semibold text-white" data-testid="user-name">
                {profile.full_name}
              </p>
              <p className="text-xs text-neutral-400" data-testid="user-role">
                {(() => {
                  const roleLabel = t(`user_role.${profile.role}`)
                  return roleLabel === `user_role.${profile.role}` ? profile.role : roleLabel
                })()}
              </p>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="secondary"
                size="sm"
                fullWidth
                onClick={handleLogout}
                data-testid="logout-button"
              >
                {t('common.logout')}
              </Button>
              <LanguageSwitcher />
            </div>
          </div>
        ) : null}
      </aside>
      <main className="flex-1 overflow-auto p-8">
        <div className="absolute right-4 top-4 z-40">
          <NotificationsBell />
        </div>
        <Outlet />
      </main>
      <PendingSalesBanner />
    </div>
  )
}
